import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { workspaceService } from '../services/workspaceService';
import { useWorkspaceOverview } from '../hooks/useWorkspaceOverview';
import { convertCurrency, formatCurrency } from '@/modules/shared/utils/formatCurrency';
import { programRoutes } from '@/modules/shared/constants/routes';
import { buildRuntimeDrawerUrl } from '../components/runtime/RuntimeFormDrawer';
import { useToast } from '@/modules/shared/components/ui/toast';
import type {
  WorkspaceInitializationDto,
  WorkspaceCurrencyContextDto,
  WorkspaceEntityHierarchyDto,
  WorkspaceOverviewRequestDto,
  WorkspaceSummaryDto,
  WorkspaceActivityLogDto,
  WorkspaceLedgerEntryDto,
  WorkspaceSelectedEntity,
  WorkspacePanelState,
  WorkspaceViewMode,
  WorkspaceCreateMode,
  WorkspaceQuickAction,
} from '../types/workspace';

// ─────────────────────────────────────────────────────────────
// Context Type
// ─────────────────────────────────────────────────────────────

interface WorkspaceContextType {
  // Workspace Initialization State
  workspace: WorkspaceInitializationDto | null;
  isInitializing: boolean;
  initializationError: string | null;

  // Program Context (from workspace)
  programId: string | null;
  programName: string | null;
  programRoleId: string | null;
  programRoleName: string | null;
  roleCategory: string | null;
  currencyContext: WorkspaceCurrencyContextDto | null;
  displayCurrency: string;
  setDisplayCurrency: (currencyCode: string) => void;
  formatCurrencyAmount: (amount: number | null | undefined, sourceCurrencyCode?: string | null) => string;

  // Entity Hierarchy (now from unified overview)
  entityHierarchy: WorkspaceEntityHierarchyDto | null;
  isLoadingEntities: boolean;
  entitiesError: string | null;

  // Overview refresh state
  overviewSummary: WorkspaceSummaryDto | null;
  isRefreshingOverview: boolean;
  lastUpdatedUtc: string | null;
  refetchOverview: () => void;

  // Filters
  filter: WorkspaceOverviewRequestDto;
  setFilter: (filter: Partial<WorkspaceOverviewRequestDto>) => void;
  resetFilter: () => void;

  // Selected Entity
  selectedEntity: WorkspaceSelectedEntity | null;
  selectEntity: (entity: WorkspaceSelectedEntity | null) => void;

  // Create Mode (Navigation to Form Pages)
  createMode: WorkspaceCreateMode;
  startCreate: (
    entityType: 'hospital' | 'hcp' | 'patient' | 'case' | 'pharmacy',
    parentId?: string,
    parentType?: 'hospital' | 'hcp' | 'patient',
    parentName?: string,
    hospitalId?: string // Required for patient creation (grandparent context)
  ) => void;
  cancelCreate: () => void;

  // Create Enrollment Case (specific handler for case creation within workspace)
  startCreateAssistanceCase: (
    patientId: string,
    hospitalId: string,
    hcpId: string
  ) => void;

  // Edit Mode (Navigation to Edit Form Pages)
  startEdit: (
    entityType: 'hospital' | 'hcp' | 'patient',
    entityId: string,
    parentIds?: { hospitalId?: string; hcpId?: string }
  ) => void;

  // Panel Visibility
  panelState: WorkspacePanelState;
  togglePanel: (panel: keyof WorkspacePanelState) => void;

  // View Mode
  viewMode: WorkspaceViewMode;
  setViewMode: (mode: WorkspaceViewMode) => void;

  // KPI nav requests — signal from KPI cards to the entity grid
  kpiNavRequest: 'hcps' | null;
  setKpiNavRequest: (req: 'hcps' | null) => void;

  // Activity & Ledger
  activity: WorkspaceActivityLogDto[];
  ledger: WorkspaceLedgerEntryDto[];
  isLoadingActivity: boolean;
  isLoadingLedger: boolean;

  // Actions
  refreshWorkspace: () => Promise<void>;
  refreshEntities: () => Promise<void>;
  refreshActivity: () => Promise<void>;
  refreshLedger: () => Promise<void>;

  // Permission Checks
  canAccessMenu: (menuKey: string) => boolean;
  canViewStep: (stepId: string) => boolean;
  canEditStep: (stepId: string) => boolean;
  canTransitionStep: (stepId: string) => boolean;

  // Quick Actions (Workflow Approvals)
  getQuickActions: (entityType: 'hospital' | 'hcp' | 'patient' | 'case', currentStepId: string) => WorkspaceQuickAction[];
  isActionableItem: (currentStepId: string) => boolean;
}

const getDefaultFilter = (): WorkspaceOverviewRequestDto => {
  const storedPageSize = typeof window !== 'undefined' ? Number(localStorage.getItem('workspace-page-size')) : 0;
  const pageSize = storedPageSize === 20 || storedPageSize === 50 || storedPageSize === 100 ? storedPageSize : 20;
  return { page: 1, pageSize, sortDescending: true };
};

const defaultPanelState: WorkspacePanelState = {
  filterPanel: false,
  detailPanel: false,
  activityPanel: false,
};

const defaultCreateMode: WorkspaceCreateMode = {
  active: false,
  entityType: null,
};

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

interface WorkspaceProviderProps {
  programId: string;
  children: ReactNode;
}

export function WorkspaceProvider({ programId, children }: WorkspaceProviderProps) {
  // Navigation and Toast
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Local State
  const [filter, setFilterState] = useState<WorkspaceOverviewRequestDto>(getDefaultFilter);
  const [selectedEntity, setSelectedEntity] = useState<WorkspaceSelectedEntity | null>(null);
  const [panelState, setPanelState] = useState<WorkspacePanelState>(defaultPanelState);
  // Initialize view mode from localStorage or default to 'hierarchy'
  const [viewMode, setViewModeState] = useState<WorkspaceViewMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(`workspace-view-mode:${programId}`);
      if (stored === 'hierarchy' || stored === 'patient-first' || stored === 'flat' || stored === 'kanban') {
        return stored;
      }
    }
    return 'patient-first';
  });
  const [createMode, setCreateMode] = useState<WorkspaceCreateMode>(defaultCreateMode);
  const [displayCurrencyState, setDisplayCurrencyState] = useState('USD');
  const [kpiNavRequest, setKpiNavRequest] = useState<'hcps' | null>(null);

  // Persist view mode to localStorage
  const setViewMode = useCallback((mode: WorkspaceViewMode) => {
    setViewModeState(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`workspace-view-mode:${programId}`, mode);
    }
  }, [programId]);

  // ─────────────────────────────────────────────────────────────
  // Workspace Initialization Query
  // ─────────────────────────────────────────────────────────────

  const {
    data: workspaceData,
    isLoading: isInitializing,
    error: workspaceError,
    refetch: refetchWorkspace,
  } = useQuery({
    queryKey: ['workspace', 'initialization', programId],
    queryFn: () => workspaceService.initializeWorkspace(programId),
    enabled: !!programId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const workspace = workspaceData?.success ? workspaceData.data : null;
  const currencyContext = workspace?.currencyContext ?? null;
  const initializationError = workspaceError
    ? 'Failed to initialize workspace'
    : workspaceData && !workspaceData.success
    ? workspaceData.message
    : null;

  const availableCurrencyCodes = useMemo(
    () => currencyContext?.currencies?.map((currency) => currency.currencyCode.toUpperCase()) ?? [],
    [currencyContext]
  );

  useEffect(() => {
    const storageKey = `workspace-display-currency:${programId}`;
    const fallbackCurrency = (
      currencyContext?.defaultCurrencyCode ||
      currencyContext?.baseCurrencyCode ||
      'USD'
    ).toUpperCase();
    const storedCurrency = typeof window !== 'undefined'
      ? window.localStorage.getItem(storageKey)?.toUpperCase()
      : null;
    const nextCurrency = storedCurrency &&
      (availableCurrencyCodes.length === 0 || availableCurrencyCodes.includes(storedCurrency))
        ? storedCurrency
        : fallbackCurrency;

    setDisplayCurrencyState(nextCurrency);
  }, [availableCurrencyCodes, currencyContext, programId]);

  const setDisplayCurrency = useCallback((currencyCode: string) => {
    const normalizedCurrencyCode = currencyCode.toUpperCase();
    setDisplayCurrencyState(normalizedCurrencyCode);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(`workspace-display-currency:${programId}`, normalizedCurrencyCode);
    }
  }, [programId]);

  const formatCurrencyAmount = useCallback(
    (amount: number | null | undefined, sourceCurrencyCode?: string | null) => {
      const baseCurrencyCode = currencyContext?.baseCurrencyCode?.toUpperCase() || 'USD';
      const targetCurrencyCode = (displayCurrencyState || currencyContext?.defaultCurrencyCode || baseCurrencyCode).toUpperCase();
      const convertedAmount = convertCurrency(amount, sourceCurrencyCode || baseCurrencyCode, targetCurrencyCode, {
        baseCurrencyCode,
        rates: currencyContext?.rates,
      });

      return formatCurrency(convertedAmount, targetCurrencyCode);
    },
    [currencyContext, displayCurrencyState]
  );

  // ─────────────────────────────────────────────────────────────
  // Unified Overview Query (replaces separate entities + stats queries)
  // ─────────────────────────────────────────────────────────────

  const {
    summary: overviewSummary,
    list: overviewList,
    lastUpdatedUtc,
    isLoading: isLoadingEntities,
    isRefetching: isRefreshingOverview,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useWorkspaceOverview(programId, filter, { enabled: !!workspace });

  // Build a WorkspaceEntityHierarchyDto-compatible object from the overview list
  // for backward compatibility with components that consume entityHierarchy
  const entityHierarchy: WorkspaceEntityHierarchyDto | null = overviewList
    ? {
        programId,
        hospitals: overviewList.items,
        totalCount: overviewList.totalCount,
        page: overviewList.page,
        pageSize: overviewList.pageSize,
      }
    : null;

  // Patch workspace.stats to reflect the filter-scoped summary counts so KpiBar stays accurate
  const workspaceWithLiveStats = useMemo(() => {
    if (!workspace) return null;
    if (!overviewSummary) return workspace;
    return {
      ...workspace,
      stats: {
        totalHospitals: overviewSummary.hospitals,
        totalHcps: overviewSummary.hcps,
        totalPatients: overviewSummary.patients,
        pendingTasks: overviewSummary.pendingTasks,
        activeCases: overviewSummary.activeCases,
      },
    };
  }, [workspace, overviewSummary]);

  // ─────────────────────────────────────────────────────────────
  // Activity Query
  // ─────────────────────────────────────────────────────────────

  const {
    data: activityData,
    isLoading: isLoadingActivity,
    refetch: refetchActivity,
  } = useQuery({
    queryKey: ['workspace', 'activity', programId],
    queryFn: () => workspaceService.getRecentActivity(programId, 50),
    enabled: !!programId && !!workspace && panelState.activityPanel,
    staleTime: 30 * 1000,
  });

  const activity = activityData?.success ? activityData.data ?? [] : [];

  // ─────────────────────────────────────────────────────────────
  // Ledger Query
  // ─────────────────────────────────────────────────────────────

  const {
    data: ledgerData,
    isLoading: isLoadingLedger,
    refetch: refetchLedger,
  } = useQuery({
    queryKey: ['workspace', 'ledger', programId],
    queryFn: () => workspaceService.getRecentLedger(programId, 50),
    enabled: !!programId && !!workspace && panelState.activityPanel,
    staleTime: 30 * 1000,
  });

  const ledger = ledgerData?.success ? ledgerData.data ?? [] : [];

  // ─────────────────────────────────────────────────────────────
  // Filter Management
  // ─────────────────────────────────────────────────────────────

  const setFilter = useCallback((partialFilter: Partial<WorkspaceOverviewRequestDto>) => {
    setFilterState((prev) => ({
      ...prev,
      ...partialFilter,
      // Reset to page 1 if any filter except page changes
      page: partialFilter.page ?? 1,
    }));
  }, []);

  const resetFilter = useCallback(() => {
    setFilterState(getDefaultFilter());
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Entity Selection
  // ─────────────────────────────────────────────────────────────

  const selectEntity = useCallback((entity: WorkspaceSelectedEntity | null) => {
    setSelectedEntity(entity);
    if (entity) {
      setPanelState((prev) => ({ ...prev, detailPanel: true }));
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Panel Management
  // ─────────────────────────────────────────────────────────────

  const togglePanel = useCallback((panel: keyof WorkspacePanelState) => {
    setPanelState((prev) => ({ ...prev, [panel]: !prev[panel] }));
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Refresh Actions
  // ─────────────────────────────────────────────────────────────

  const refreshWorkspace = useCallback(async () => {
    await refetchWorkspace();
  }, [refetchWorkspace]);

  const refreshEntities = useCallback(async () => {
    refetchOverview();
  }, [refetchOverview]);

  const refreshActivity = useCallback(async () => {
    await refetchActivity();
  }, [refetchActivity]);

  const refreshLedger = useCallback(async () => {
    await refetchLedger();
  }, [refetchLedger]);

  // ─────────────────────────────────────────────────────────────
  // Permission Checks
  // ─────────────────────────────────────────────────────────────

  const canAccessMenu = useCallback(
    (menuKey: string) => {
      if (!workspace) return false;
      return workspace.accessibleMenuKeys.includes(menuKey);
    },
    [workspace]
  );

  const canViewStep = useCallback(
    (stepId: string) => {
      if (!workspace) return false;
      const mapping = workspace.roleStepMappings.find((m) => m.stepId === stepId);
      return mapping?.canView ?? false;
    },
    [workspace]
  );

  const canEditStep = useCallback(
    (stepId: string) => {
      if (!workspace) return false;
      const mapping = workspace.roleStepMappings.find((m) => m.stepId === stepId);
      return mapping?.canEdit ?? false;
    },
    [workspace]
  );

  const canTransitionStep = useCallback(
    (stepId: string) => {
      if (!workspace) return false;
      const mapping = workspace.roleStepMappings.find((m) => m.stepId === stepId);
      return mapping?.canTransition ?? false;
    },
    [workspace]
  );

  // ─────────────────────────────────────────────────────────────
  // Create Mode (Navigation to Form Pages)
  // ─────────────────────────────────────────────────────────────

  const startCreate = useCallback(
    (
      entityType: 'hospital' | 'hcp' | 'patient' | 'case' | 'pharmacy',
      parentId?: string,
      _parentType?: 'hospital' | 'hcp' | 'patient',
      _parentName?: string,
      hospitalId?: string // Required for patient creation
    ) => {
      // Navigate to the workspace path with runtime drawer query params so the
      // user stays anchored on the workspace and the form opens as an overlay.
      let url = '';
      switch (entityType) {
        case 'hospital':
          url = buildRuntimeDrawerUrl(programId, 'hospital', 'create');
          break;
        case 'hcp':
          url = buildRuntimeDrawerUrl(programId, 'hcp', 'create', undefined, {
            ...(parentId ? { hospitalId: parentId } : {}),
          });
          break;
        case 'patient':
          // parentId = hcpId, hospitalId = hospital.
          // Hospital and HCP are optional here — the registration form includes
          // Hospital and HCP dropdowns so CC users can select them while entering
          // patient details. Validation only occurs on form submit.
          url = buildRuntimeDrawerUrl(programId, 'patient', 'create', undefined, {
            ...(hospitalId ? { hospitalId } : {}),
            ...(parentId ? { hcpId: parentId } : {}),
          });
          break;
        case 'case':
          showToast('Please use the patient action menu to create an assistance case.', 'info');
          return;
        case 'pharmacy':
          url = buildRuntimeDrawerUrl(programId, 'pharmacy', 'create');
          break;
      }

      navigate(url);
    },
    [programId, navigate, showToast]
  );

  // Start Create Assistance Case (specific handler for nested workspace route)
  const startCreateAssistanceCase = useCallback(
    (patientId: string, hospitalId: string, hcpId: string) => {
      if (!programId) {
        showToast('Program context required.', 'error');
        return;
      }
      // Navigate to nested workspace assistance case create route
      const url = programRoutes.workspaceAssistanceCreate(programId, hospitalId, hcpId, patientId);
      navigate(url);
    },
    [programId, navigate, showToast]
  );

  const cancelCreate = useCallback(() => {
    setCreateMode(defaultCreateMode);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Edit Mode (Navigation to Edit Form Pages)
  // ─────────────────────────────────────────────────────────────

  const startEdit = useCallback(
    (
      entityType: 'hospital' | 'hcp' | 'patient',
      entityId: string,
      parentIds?: { hospitalId?: string; hcpId?: string }
    ) => {
      if (!programId) return;

      let url = '';
      switch (entityType) {
        case 'hospital':
          url = buildRuntimeDrawerUrl(programId, 'hospital', 'edit', entityId);
          break;
        case 'hcp':
          if (!parentIds?.hospitalId) {
            showToast('Hospital context required to edit HCP.', 'error');
            return;
          }
          url = buildRuntimeDrawerUrl(programId, 'hcp', 'edit', entityId, {
            hospitalId: parentIds.hospitalId,
          });
          break;
        case 'patient':
          if (!parentIds?.hospitalId || !parentIds?.hcpId) {
            showToast('Hospital and HCP context required to edit Patient.', 'error');
            return;
          }
          url = buildRuntimeDrawerUrl(programId, 'patient', 'edit', entityId, {
            hospitalId: parentIds.hospitalId,
            hcpId: parentIds.hcpId,
          });
          break;
      }

      navigate(url);
    },
    [programId, navigate, showToast]
  );

  // ─────────────────────────────────────────────────────────────
  // Quick Actions (Workflow-Aware Approvals)
  // ─────────────────────────────────────────────────────────────

  const getQuickActions = useCallback(
    (_entityType: 'hospital' | 'hcp' | 'patient' | 'case', currentStepId: string): WorkspaceQuickAction[] => {
      if (!workspace) return [];

      // Find available transitions from current step
      const transitions = workspace.activeWorkflow?.transitions?.filter(
        (t) => t.fromStepId === currentStepId
      ) ?? [];

      // Check if user can transition at this step
      const canTransition = canTransitionStep(currentStepId);
      if (!canTransition) return [];

      // Map transitions to quick actions
      return transitions.map((t) => {
        // Look up the step name from workflow steps
        const toStep = workspace.activeWorkflow?.steps?.find((s) => s.stepId === t.toStepId);
        return {
          transitionId: t.transitionId,
          actionName: t.transitionName,
          actionType: t.transitionName.toLowerCase().includes('approve') 
            ? 'approve' as const
            : t.transitionName.toLowerCase().includes('reject')
              ? 'reject' as const
              : 'transition' as const,
          requiresComment: t.transitionName.toLowerCase().includes('reject'),
          toStepId: t.toStepId,
          toStepName: toStep?.stepName ?? 'Unknown',
        };
      });
    },
    [workspace, canTransitionStep]
  );

  const isActionableItem = useCallback(
    (currentStepId: string): boolean => {
      if (!workspace) return false;
      // Item is actionable if user can transition and there are available transitions
      const canTransition = canTransitionStep(currentStepId);
      if (!canTransition) return false;

      const transitions = workspace.activeWorkflow?.transitions?.filter(
        (t) => t.fromStepId === currentStepId
      ) ?? [];

      return transitions.length > 0;
    },
    [workspace, canTransitionStep]
  );

  // ─────────────────────────────────────────────────────────────
  // Clear cache on program change
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    // Reset state when program changes
    setFilterState(getDefaultFilter());
    setSelectedEntity(null);
    setPanelState(defaultPanelState);
    setCreateMode(defaultCreateMode);
  }, [programId]);

  // ─────────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────────

  const contextValue: WorkspaceContextType = useMemo(() => ({
    // Workspace State
    workspace: workspaceWithLiveStats,
    isInitializing,
    initializationError,

    // Program Context
    programId: workspace?.programId ?? null,
    programName: workspace?.programName ?? null,
    programRoleId: workspace?.programRoleId ?? null,
    programRoleName: workspace?.programRoleName ?? null,
    roleCategory: workspace?.roleCategory ?? null,
    currencyContext,
    displayCurrency: displayCurrencyState,
    setDisplayCurrency,
    formatCurrencyAmount,

    // Entity Hierarchy
    entityHierarchy,
    isLoadingEntities,
    entitiesError: isOverviewError ? 'Failed to load entities' : null,

    // Overview refresh state
    overviewSummary,
    isRefreshingOverview,
    lastUpdatedUtc,
    refetchOverview,

    // Filters
    filter,
    setFilter,
    resetFilter,

    // Selected Entity
    selectedEntity,
    selectEntity,

    // Create Mode (Inline Creation)
    createMode,
    startCreate,
    cancelCreate,

    // Create Assistance Case
    startCreateAssistanceCase,

    // Edit Mode
    startEdit,

    // Panel Visibility
    panelState,
    togglePanel,

    // View Mode
    viewMode,
    setViewMode,

    // KPI nav requests
    kpiNavRequest,
    setKpiNavRequest,

    // Activity & Ledger
    activity,
    ledger,
    isLoadingActivity,
    isLoadingLedger,

    // Actions
    refreshWorkspace,
    refreshEntities,
    refreshActivity,
    refreshLedger,

    // Permission Checks
    canAccessMenu,
    canViewStep,
    canEditStep,
    canTransitionStep,

    // Quick Actions (Workflow Approvals)
    getQuickActions,
    isActionableItem,
  }), [
    workspaceWithLiveStats, isInitializing, initializationError,
    workspace?.programId, workspace?.programName, workspace?.programRoleId,
    workspace?.programRoleName, workspace?.roleCategory,
    currencyContext, displayCurrencyState, setDisplayCurrency, formatCurrencyAmount,
    entityHierarchy, isLoadingEntities, isOverviewError,
    overviewSummary, isRefreshingOverview, lastUpdatedUtc, refetchOverview,
    filter, setFilter, resetFilter,
    selectedEntity, selectEntity,
    createMode, startCreate, cancelCreate,
    startCreateAssistanceCase, startEdit,
    panelState, togglePanel,
    viewMode, setViewMode,
    kpiNavRequest, setKpiNavRequest,
    activity, ledger, isLoadingActivity, isLoadingLedger,
    refreshWorkspace, refreshEntities, refreshActivity, refreshLedger,
    canAccessMenu, canViewStep, canEditStep, canTransitionStep,
    getQuickActions, isActionableItem,
  ]);

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

export function useWorkspace(): WorkspaceContextType {
  const context = useOptionalWorkspace();
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

export function useOptionalWorkspace(): WorkspaceContextType | undefined {
  return useContext(WorkspaceContext);
}

// ─────────────────────────────────────────────────────────────
// Selector Hooks for Performance
// ─────────────────────────────────────────────────────────────

export function useWorkspaceStats() {
  const { workspace } = useWorkspace();
  return workspace?.stats ?? null;
}

export function useWorkspaceWorkflow() {
  const { workspace } = useWorkspace();
  return workspace?.activeWorkflow ?? null;
}

export function useWorkspaceForms() {
  const { workspace } = useWorkspace();
  return workspace?.formSchemas ?? [];
}

export function useWorkspaceAssistance() {
  const { workspace } = useWorkspace();
  return workspace?.assistanceConfig ?? null;
}
